import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from '../hooks/use-toast';
import { businessActivities, freezones as mockFreezones } from '../mock';
import { getFreezones, createApplication, costEstimate, payApplication } from '../api';
import { Building2, Globe, ChevronRight, ChevronLeft, Sparkles, CheckCircle2, CreditCard, Send } from 'lucide-react';

const STEPS = 6;
const fmt = (n) => Number(n || 0).toLocaleString('en-US');

const SetupWizard = () => {
  const { t, pick, lang, dir } = useLang();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [refNum, setRefNum] = useState(null);
  const [created, setCreated] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [freezones, setFreezones] = useState(mockFreezones);

  useEffect(() => {
    getFreezones().then((d) => d && d.length && setFreezones(d.map(z => ({ ...z, id: z.code, minCost: z.min_cost })))).catch(() => {});
  }, []);

  const [data, setData] = useState({
    type: params.get('type') || '',
    zone: '',
    activity: '',
    companyName: '',
    shareholders: '1',
    capital: '',
    visas: '1',
    office: 'flexi',
    fullName: '',
    email: '',
    phone: '',
    notes: ''
  });

  // Prefill from authenticated user
  useEffect(() => {
    if (user) {
      setData((d) => ({
        ...d,
        fullName: d.fullName || user.name || '',
        email: d.email || user.email || '',
        phone: d.phone || user.phone || ''
      }));
    }
  }, [user]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  useEffect(() => {
    if (step !== STEPS) return;
    setEstimate(null);
    costEstimate({
      type: data.type, zone_code: data.type === 'freezone' ? data.zone : null,
      visas: parseInt(data.visas, 10) || 1, office: data.office, shareholders: parseInt(data.shareholders, 10) || 1
    }).then(setEstimate).catch(() => setEstimate(null));
  }, [step, data.type, data.zone, data.visas, data.office, data.shareholders]);

  const startPayment = async (appId) => {
    setPaying(true);
    try {
      const r = await payApplication(appId);
      if (r?.checkout_url) { window.location.href = r.checkout_url; return; }
      toast({ title: lang === 'ar' ? 'تعذّر بدء الدفع' : 'Could not start payment', variant: 'destructive' });
    } catch (err) {
      toast({ title: err.response?.data?.detail || (lang === 'ar' ? 'تعذّر بدء الدفع' : 'Could not start payment'), variant: 'destructive' });
    } finally { setPaying(false); }
  };

  const canNext = useMemo(() => {
    if (step === 1) return !!data.type && (data.type === 'mainland' || !!data.zone);
    if (step === 2) return !!data.activity;
    if (step === 3) return !!data.companyName && !!data.capital;
    if (step === 4) return !!data.visas && !!data.office;
    if (step === 5) return !!data.fullName && !!data.email && !!data.phone;
    return true;
  }, [step, data]);

  const submit = async (payAfter = false) => {
    setSubmitting(true);
    try {
      const payload = {
        type: data.type,
        zone_code: data.type === 'freezone' ? data.zone : null,
        activity: data.activity,
        company_name: data.companyName,
        shareholders: parseInt(data.shareholders, 10) || 1,
        capital: data.capital,
        visas: parseInt(data.visas, 10) || 1,
        office_type: data.office,
        full_name: data.fullName,
        email: data.email,
        phone: data.phone,
        notes: data.notes || ''
      };
      const res = await createApplication(payload);
      setRefNum(res.id);
      setCreated(res);
      toast({ title: t('wizard.success'), description: t('wizard.success_sub') + ' ' + res.id });
      if (payAfter) await startPayment(res.id);
    } catch (err) {
      const detail = err.response?.data?.detail || (lang === 'ar' ? 'فشل الإرسال' : 'Submission failed');
      toast({ title: detail, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const quoteTotal = created?.estimate?.total ?? estimate?.total;

  if (refNum) {
    return (
      <div className="pt-28 pb-16 min-h-[70vh]" dir={dir}>
        <div className="max-w-2xl mx-auto px-6">
          <div className="glass-card rounded-3xl p-14 text-center" data-testid="wizard-success">
            <div className="w-20 h-20 mx-auto rounded-full bg-blue-500/15 border border-blue-400/40 flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-blue-400" />
            </div>
            <h1 className="font-display text-4xl text-[#0a0a0a]">{t('wizard.success')}</h1>
            <p className="mt-4 text-[#111827]/70">{t('wizard.success_sub')}:</p>
            <div className="mt-4 inline-block px-6 py-3 rounded-lg bg-[#2563eb]/10 border border-[#2563eb]/30 font-display text-2xl gold-text" data-testid="wizard-ref">{refNum}</div>
            {quoteTotal > 0 && (
              <div className="mt-6 rounded-xl border border-[#2563eb]/25 bg-[#f8fafc] p-5" data-testid="success-quote">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#2563eb]">{t('wizard.total_due')}</div>
                <div className="font-display text-3xl text-[#0a0a0a] mt-1 tabular-nums">{fmt(quoteTotal)} <span className="text-base text-[#111827]/50">AED</span></div>
                <p className="text-xs text-[#111827]/55 mt-2">{t('wizard.pay_later_hint')}</p>
                <Button onClick={() => startPayment(refNum)} disabled={paying} className="gold-button rounded-md h-11 px-8 text-sm mt-4" data-testid="success-pay-btn">
                  <CreditCard className="w-4 h-4 me-2" /> {paying ? '...' : t('wizard.pay_now')}
                </Button>
              </div>
            )}
            <p className="mt-6 text-sm text-[#111827]/60">{t('wizard.track')}</p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Button onClick={() => navigate('/MyApplications')} className="gold-button rounded-md h-11 px-6 text-sm">{t('nav.apps')}</Button>
              <Button onClick={() => { setRefNum(null); setCreated(null); setStep(1); setData({ type: '', zone: '', activity: '', companyName: '', shareholders: '1', capital: '', visas: '1', office: 'flexi', fullName: '', email: '', phone: '', notes: '' }); }} className="ghost-button rounded-md h-11 px-6 text-sm bg-transparent">{t('apps.new_app')}</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-16 min-h-screen" dir={dir}>
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#2563eb]/30 bg-[#2563eb]/5 mb-4">
            <Sparkles className="w-3 h-3 text-[#3b82f6]" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#3b82f6]">{t('wizard.step')} {step} {t('wizard.of')} {STEPS}</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-[#0a0a0a]">{t('wizard.title')}</h1>
          <p className="mt-3 text-[#111827]/60">{t('wizard.subtitle')}</p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="h-1 bg-[#2563eb]/10 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#2563eb] to-[#3b82f6] rounded-full transition-all duration-500" style={{ width: `${(step / STEPS) * 100}%` }} />
          </div>
        </div>

        <div className="glass-card rounded-2xl p-8 md:p-10">
          {step === 1 && (
            <div>
              <h2 className="font-display text-2xl text-[#0a0a0a] mb-2">{t('wizard.s1')}</h2>
              <p className="text-sm text-[#111827]/60 mb-6">{t('wizard.jurisdiction_q')}</p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { id: 'mainland', icon: Building2, label: t('compare.mainland'), color: '#3b82f6' },
                  { id: 'freezone', icon: Globe, label: t('compare.freezone'), color: '#60a5fa' }
                ].map((o) => (
                  <button key={o.id} onClick={() => set('type', o.id)} className={`p-6 rounded-xl border-2 transition-all text-start ${data.type === o.id ? 'border-[#2563eb] bg-[#2563eb]/8' : 'border-[#2563eb]/15 hover:border-[#2563eb]/40'}`}>
                    <o.icon className="w-8 h-8 mb-3" style={{ color: o.color }} />
                    <div className="font-display text-xl text-[#0a0a0a]">{o.label}</div>
                  </button>
                ))}
              </div>
              {data.type === 'freezone' && (
                <div className="mt-6">
                  <Label className="text-[#0a0a0a] text-sm mb-2 block">{t('calc.zone')}</Label>
                  <Select value={data.zone} onValueChange={(v) => set('zone', v)}>
                    <SelectTrigger className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-[#ffffff] border-[#2563eb]/25">
                      {freezones.map((z) => (<SelectItem key={z.id} value={z.id} className="text-[#111827]">{pick(z.name)}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-display text-2xl text-[#0a0a0a] mb-2">{t('wizard.s2')}</h2>
              <p className="text-sm text-[#111827]/60 mb-6">{t('wizard.activity_q')}</p>
              <div className="grid grid-cols-2 gap-3">
                {businessActivities.map((a) => (
                  <button key={a.id} onClick={() => set('activity', a.id)} className={`p-4 rounded-lg border text-sm transition-all text-start ${data.activity === a.id ? 'border-[#2563eb] bg-[#2563eb]/8 text-[#3b82f6]' : 'border-[#2563eb]/15 text-[#111827]/80 hover:border-[#2563eb]/40'}`}>
                    {a[lang]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-display text-2xl text-[#0a0a0a] mb-6">{t('wizard.s3')}</h2>
              <div className="space-y-5">
                <div>
                  <Label className="text-[#0a0a0a] text-sm mb-2 block">{t('wizard.company_name')}</Label>
                  <Input value={data.companyName} onChange={(e) => set('companyName', e.target.value)} className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827] focus-visible:ring-[#2563eb]/40" />
                </div>
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <Label className="text-[#0a0a0a] text-sm mb-2 block">{t('wizard.shareholders')}</Label>
                    <Input type="number" min="1" value={data.shareholders} onChange={(e) => set('shareholders', e.target.value)} className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                  <div>
                    <Label className="text-[#0a0a0a] text-sm mb-2 block">{t('wizard.capital')}</Label>
                    <Input value={data.capital} onChange={(e) => set('capital', e.target.value)} placeholder="50,000" className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="font-display text-2xl text-[#0a0a0a] mb-6">{t('wizard.s4')}</h2>
              <div className="space-y-6">
                <div>
                  <Label className="text-[#0a0a0a] text-sm mb-2 block">{t('wizard.visas_needed')}</Label>
                  <Input type="number" min="1" max="50" value={data.visas} onChange={(e) => set('visas', e.target.value)} className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
                </div>
                <div>
                  <Label className="text-[#0a0a0a] text-sm mb-3 block">{t('wizard.office_type')}</Label>
                  <RadioGroup value={data.office} onValueChange={(v) => set('office', v)} className="space-y-2">
                    {[
                      { id: 'flexi', label: t('wizard.flexi') },
                      { id: 'private', label: t('wizard.private_office') },
                      { id: 'executive', label: t('wizard.executive') }
                    ].map((o) => (
                      <div key={o.id} className="flex items-center gap-3 p-4 rounded-lg border border-[#2563eb]/15 hover:border-[#2563eb]/40 cursor-pointer" onClick={() => set('office', o.id)}>
                        <RadioGroupItem value={o.id} className="border-[#2563eb]/40 text-[#2563eb]" />
                        <span className="text-[#111827]/85 text-sm">{o.label}</span>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <h2 className="font-display text-2xl text-[#0a0a0a] mb-6">{t('wizard.s5')}</h2>
              <div className="space-y-5">
                <div>
                  <Label className="text-[#0a0a0a] text-sm mb-2 block">{t('wizard.full_name')}</Label>
                  <Input value={data.fullName} onChange={(e) => set('fullName', e.target.value)} className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
                </div>
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <Label className="text-[#0a0a0a] text-sm mb-2 block">{t('wizard.email')}</Label>
                    <Input type="email" value={data.email} onChange={(e) => set('email', e.target.value)} className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                  <div>
                    <Label className="text-[#0a0a0a] text-sm mb-2 block">{t('wizard.phone')}</Label>
                    <Input value={data.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+971 ..." className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#0a0a0a] text-sm mb-2 block">{t('wizard.notes')}</Label>
                  <Textarea rows={4} value={data.notes} onChange={(e) => set('notes', e.target.value)} className="bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
                </div>
              </div>
            </div>
          )}

          {step === 6 && (
            <div>
              <h2 className="font-display text-2xl text-[#0a0a0a] mb-6">{t('wizard.review_title')}</h2>
              <div className="space-y-3">
                {[
                  { k: t('wizard.s1'), v: (data.type === 'mainland' ? t('compare.mainland') : `${t('compare.freezone')} – ${pick(freezones.find(z => z.id === data.zone)?.name) || '—'}`) },
                  { k: t('wizard.s2'), v: businessActivities.find(a => a.id === data.activity)?.[lang] || '—' },
                  { k: t('wizard.company_name'), v: data.companyName },
                  { k: t('wizard.capital'), v: data.capital + ' AED' },
                  { k: t('wizard.visas_needed'), v: data.visas },
                  { k: t('wizard.office_type'), v: t('wizard.' + (data.office === 'private' ? 'private_office' : data.office)) },
                  { k: t('wizard.full_name'), v: data.fullName },
                  { k: t('wizard.email'), v: data.email },
                  { k: t('wizard.phone'), v: data.phone }
                ].map((r, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-[#2563eb]/10 last:border-0">
                    <span className="text-xs uppercase tracking-widest text-[#111827]/50">{r.k}</span>
                    <span className="text-sm text-[#0a0a0a]">{r.v}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 rounded-2xl border border-[#2563eb]/25 bg-[#f8fafc] p-6" data-testid="wizard-quote">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-xl text-[#0a0a0a]">{t('wizard.quote_title')}</h3>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#2563eb]">{t('calc.breakdown')}</span>
                </div>
                {!estimate ? (
                  <div className="text-sm text-[#111827]/50 py-4 text-center">…</div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {[
                        ['license', t('calc.license')], ['office', t('calc.office_fee')], ['visa', t('calc.visa_fee')],
                        ['admin', t('calc.admin')], ['tax', t('calc.tax_reg')]
                      ].map(([k, label]) => (
                        <div key={k} className="flex items-center justify-between text-sm" data-testid={`quote-${k}`}>
                          <span className="text-[#111827]/65">{label}</span>
                          <span className="text-[#0a0a0a] tabular-nums">{fmt(estimate[k])} AED</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#2563eb]/20">
                      <span className="text-xs uppercase tracking-widest text-[#2563eb] font-bold">{t('calc.total')}</span>
                      <span className="font-display text-3xl text-[#0a0a0a] tabular-nums" data-testid="quote-total">{fmt(estimate.total)} <span className="text-base text-[#111827]/50">AED</span></span>
                    </div>
                  </>
                )}
                <p className="text-[11px] text-[#111827]/50 mt-3">{t('wizard.quote_note')}</p>
              </div>
            </div>
          )}

          {/* Nav Buttons */}
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-[#2563eb]/10">
            <Button onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} className="ghost-button rounded-md h-11 px-5 text-sm bg-transparent disabled:opacity-30">
              <ChevronLeft className="w-4 h-4 me-1 rtl:hidden" />
              <ChevronRight className="w-4 h-4 me-1 hidden rtl:inline" />
              {t('wizard.back')}
            </Button>
            {step < STEPS ? (
              <Button onClick={() => canNext && setStep((s) => Math.min(STEPS, s + 1))} disabled={!canNext} className="gold-button rounded-md h-11 px-6 text-sm disabled:opacity-40">
                {t('wizard.next')}
                <ChevronRight className="w-4 h-4 ms-1 rtl:hidden" />
                <ChevronLeft className="w-4 h-4 ms-1 hidden rtl:inline" />
              </Button>
            ) : (
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Button onClick={() => submit(false)} disabled={submitting || paying} className="ghost-button rounded-md h-11 px-5 text-sm bg-transparent" data-testid="wizard-submit-btn">
                  <Send className="w-4 h-4 me-1" /> {submitting && !paying ? '...' : t('wizard.submit_only')}
                </Button>
                <Button onClick={() => submit(true)} disabled={submitting || paying || !estimate} className="gold-button rounded-md h-11 px-6 text-sm" data-testid="wizard-submit-pay-btn">
                  <CreditCard className="w-4 h-4 me-1" /> {paying ? '...' : `${t('wizard.submit_pay')}${estimate ? ` · ${fmt(estimate.total)} AED` : ''}`}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
